import { formatScaleFactor } from './quantity.js';
import type { CookDate, CookDateId, Recipe, RecipeId } from './types.js';
import { localCalendarDay } from './validation.js';

export interface CookDateSummary {
  readonly timesMade: number;
  readonly lastMade: string | null;
  readonly plannedFor: string | null;
}

/**
 * Summarises a cook log relative to a local calendar day.
 *
 * Dates on or before `today` count as made; dates after count as planned.
 */
export function summariseCookDates(
  cookDates: readonly CookDate[],
  today: string = localCalendarDay(),
): CookDateSummary {
  let timesMade = 0;
  let lastMade: string | null = null;
  let plannedFor: string | null = null;

  for (const entry of cookDates) {
    if (entry.date <= today) {
      timesMade += 1;
      if (lastMade == null || entry.date > lastMade) lastMade = entry.date;
    } else if (plannedFor == null || entry.date < plannedFor) {
      plannedFor = entry.date;
    }
  }

  return { timesMade, lastMade, plannedFor };
}

/** Newest calendar day first; ties broken by id for stability. */
export function sortCookDatesNewestFirst(cookDates: readonly CookDate[]): CookDate[] {
  return [...cookDates].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? 1 : -1;
    return a.id.localeCompare(b.id);
  });
}

/**
 * Every logged day on or before `today`, newest first — the entries `timesMade` counts.
 * Duplicate same-day logs stay separate so the list length matches the count.
 */
export function madeCookDates(
  cookDates: readonly CookDate[],
  today: string = localCalendarDay(),
): CookDate[] {
  return sortCookDatesNewestFirst(cookDates.filter((entry) => entry.date <= today));
}

/** "scaled 2×" for a cook date made at a scaled batch size, or null when it was the usual 1×. */
export function formatCookDateScale(cookDate: CookDate): string | null {
  return cookDate.scale == null ? null : `scaled ${formatScaleFactor(cookDate.scale)}`;
}

/** One recipe on one calendar day (duplicate same-day logs collapse into cookDateIds). */
export interface ScheduleEntry {
  readonly recipeId: RecipeId;
  readonly title: string;
  readonly date: string;
  readonly cookDateIds: readonly CookDateId[];
}

export interface MonthCell {
  /** YYYY-MM-DD when this cell is a real day; null for empty padding. */
  readonly date: string | null;
  readonly inMonth: boolean;
}

export interface YearMonth {
  readonly year: number;
  readonly month: number;
}

/** Index cook dates across recipes: day → entries sorted by title then recipe id. */
export function indexScheduleByDay(recipes: readonly Recipe[]): Map<string, ScheduleEntry[]> {
  const byDay = new Map<string, Map<RecipeId, ScheduleEntry>>();

  for (const recipe of recipes) {
    if (recipe.deletedAt != null) continue;
    for (const cookDate of recipe.cookDates) {
      let dayMap = byDay.get(cookDate.date);
      if (dayMap == null) {
        dayMap = new Map();
        byDay.set(cookDate.date, dayMap);
      }
      const existing = dayMap.get(recipe.id);
      if (existing == null) {
        dayMap.set(recipe.id, {
          recipeId: recipe.id,
          title: recipe.title,
          date: cookDate.date,
          cookDateIds: [cookDate.id],
        });
      } else {
        dayMap.set(recipe.id, {
          ...existing,
          cookDateIds: [...existing.cookDateIds, cookDate.id],
        });
      }
    }
  }

  const result = new Map<string, ScheduleEntry[]>();
  for (const [date, dayMap] of byDay) {
    const entries = [...dayMap.values()].sort((a, b) => {
      const byTitle = a.title.localeCompare(b.title);
      return byTitle !== 0 ? byTitle : a.recipeId.localeCompare(b.recipeId);
    });
    result.set(date, entries);
  }
  return result;
}

/**
 * Fixed 6×7 Sun–Sat grid for a calendar month.
 * `month` is 1–12. Out-of-month days still carry a date for adjacent-month display.
 */
export function monthGrid(year: number, month: number): MonthCell[] {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const startWeekday = first.getUTCDay(); // 0 = Sunday
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const cells: MonthCell[] = [];

  for (let i = 0; i < 42; i += 1) {
    const dayOffset = i - startWeekday + 1;
    if (dayOffset < 1 || dayOffset > daysInMonth) {
      const probe = new Date(Date.UTC(year, month - 1, dayOffset));
      cells.push({
        date: formatUtcDay(probe),
        inMonth: false,
      });
    } else {
      cells.push({
        date: formatYearMonthDay(year, month, dayOffset),
        inMonth: true,
      });
    }
  }
  return cells;
}

/** `month` is 1–12. */
export function shiftMonth(year: number, month: number, delta: number): YearMonth {
  const d = new Date(Date.UTC(year, month - 1 + delta, 1));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
}

/** English month label, e.g. "March 2024". `month` is 1–12. */
export function formatMonthLabel(year: number, month: number): string {
  const d = new Date(Date.UTC(year, month - 1, 1));
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** Year/month for a YYYY-MM-DD day (or today). */
export function yearMonthFromDay(day: string = localCalendarDay()): YearMonth {
  const [y, m] = day.split('-').map(Number) as [number, number];
  return { year: y, month: m };
}

/** Chunk a 42-cell month grid into 6 Sunday–Saturday weeks. */
export function monthWeeks(cells: readonly MonthCell[]): MonthCell[][] {
  const weeks: MonthCell[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

/** Sunday (YYYY-MM-DD) of the week containing `day`. */
export function weekSunday(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() - date.getUTCDay());
  return formatUtcDay(date);
}

/** Shift a YYYY-MM-DD by `deltaDays` calendar days. */
export function shiftDay(day: string, deltaDays: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return formatUtcDay(new Date(Date.UTC(y, m - 1, d + deltaDays)));
}

/** One Sun–Sat week of cells starting at `sunday` (YYYY-MM-DD). */
export function weekCells(sunday: string): MonthCell[] {
  return Array.from({ length: 7 }, (_, i) => ({
    date: shiftDay(sunday, i),
    inMonth: true,
  }));
}

/** English month + day for a YYYY-MM-DD, e.g. "August 10". */
export function formatMonthDayLabel(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const parsed = new Date(Date.UTC(y, m - 1, d));
  return parsed.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Short weekday labels, Sunday-first (matches `monthGrid`). */
export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** How long a pending schedule date stays active for Cook Date auto-fill. */
export const PENDING_SCHEDULE_DATE_TTL_MS = 60 * 60 * 1000;

interface PendingScheduleDate {
  readonly date: string;
  readonly setAt: number;
}

let pendingScheduleDate: PendingScheduleDate | null = null;

/** Remember a calendar day so Cook Date can auto-assign a recipe to it. */
export function setPendingScheduleDate(date: string, now: number = Date.now()): void {
  pendingScheduleDate = { date, setAt: now };
}

/** Clear any remembered schedule day (Recipes tab, expiry, or after use). */
export function clearPendingScheduleDate(): void {
  pendingScheduleDate = null;
}

/** Peek at the pending date if still within the TTL; otherwise clear and return null. */
export function peekPendingScheduleDate(now: number = Date.now()): string | null {
  if (pendingScheduleDate == null) return null;
  if (now - pendingScheduleDate.setAt > PENDING_SCHEDULE_DATE_TTL_MS) {
    pendingScheduleDate = null;
    return null;
  }
  return pendingScheduleDate.date;
}

/** Peek and clear in one step (Cook Date consume). */
export function takePendingScheduleDate(now: number = Date.now()): string | null {
  const date = peekPendingScheduleDate(now);
  pendingScheduleDate = null;
  return date;
}

/** Days before today offered as one-click rows in the Cook Date view. */
export const COOK_DATE_WINDOW_PAST = 1;

/** Days after today offered as one-click rows in the Cook Date view. */
export const COOK_DATE_WINDOW_FUTURE = 10;

/** One offered day in the Cook Date view, with whatever is already logged against it. */
export interface CookDateWindowRow {
  readonly date: string;
  /** Every log on this day; empty when the day is not logged. */
  readonly cookDateIds: readonly CookDateId[];
  readonly selected: boolean;
  readonly isToday: boolean;
  readonly isFuture: boolean;
}

/** Group a cook log by calendar day, collapsing duplicate same-day logs. */
export function groupCookDatesByDay(cookDates: readonly CookDate[]): Map<string, CookDateId[]> {
  const byDay = new Map<string, CookDateId[]>();
  for (const entry of cookDates) {
    const group = byDay.get(entry.date);
    if (group == null) byDay.set(entry.date, [entry.id]);
    else group.push(entry.id);
  }
  return byDay;
}

/** Every log on one calendar day; empty when that day is not logged. */
export function cookDateIdsOn(cookDates: readonly CookDate[], date: string): CookDateId[] {
  return cookDates.filter((entry) => entry.date === date).map((entry) => entry.id);
}

/**
 * The rows both the web dialog and the mobile sheet render: a window of calendar days
 * around `today`, each carrying its own logs so one click can toggle the day on or off.
 *
 * Logged days outside the window get no row — the summary line above the list stands in
 * for them, and the date picker below the list still reaches them.
 */
export function cookDateWindowRows(
  cookDates: readonly CookDate[],
  today: string = localCalendarDay(),
  past: number = COOK_DATE_WINDOW_PAST,
  future: number = COOK_DATE_WINDOW_FUTURE,
): CookDateWindowRow[] {
  const byDay = groupCookDatesByDay(cookDates);
  const rows: CookDateWindowRow[] = [];

  for (let offset = -past; offset <= future; offset += 1) {
    const date = shiftDay(today, offset);
    const cookDateIds = byDay.get(date) ?? [];
    rows.push({
      date,
      cookDateIds,
      selected: cookDateIds.length > 0,
      isToday: date === today,
      isFuture: date > today,
    });
  }
  return rows;
}

/** Full cook day for summaries, e.g. "Sep 20, 2026". */
export function formatCookDay(date: string): string {
  return cookDayAsLocalDate(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * What the Cook Date views say once a day has been logged.
 *
 * Echoes the wording of the control the user just touched — the rows are labelled
 * "Plan for …" and "Mark made on …" — and lives here so the two platforms cannot drift
 * apart on the copy.
 */
export function cookDateSavedMessage(date: string, today: string = localCalendarDay()): string {
  return date > today
    ? `Planned for ${formatCookDay(date)}`
    : `Marked made on ${formatCookDay(date)}`;
}

/** Compact cook day for list rows, e.g. "Sat, Sep 20". */
export function formatCookDayShort(date: string): string {
  return cookDayAsLocalDate(date).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** A YYYY-MM-DD cook day read in the viewer's own timezone, so labels match the calendar. */
function cookDayAsLocalDate(date: string): Date {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  return new Date(year, month - 1, day);
}

function formatUtcDay(d: Date): string {
  return formatYearMonthDay(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

function formatYearMonthDay(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
